import { OtlDateFormField as DateFormField } from "./OtlDateFormField";
import { OtlTextFormField as TextFormField } from "./OtlTextFormField";
import { Box, Button, Grid, Stack, Typography } from "@mui/material";
import { useFormState } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import { CheckboxFormField } from "../common/components/FormFields";
import { ListOption } from "../common/api/Interfaces/listOption";
import {
  OtlAddress,
  OtlFormSubmission,
  OtlHouseholdMember,
} from "./OtlFormSubmission";
import { OtlAddressFields } from "./OtlAddressFields";
import { MuiAdd } from "../common/icons";
import { CANADIAN_PROVINCES } from "../common/api/provinceApi";
import DeleteIcon from "@mui/icons-material/Delete";

interface HouseholdMemberFieldsProps {
  name: string;
  member: OtlHouseholdMember;
  communities?: ListOption[];
  isCommunitiesLoading?: boolean;
}

function HouseholdMemberFields({
  name,
  member,
  communities,
  isCommunitiesLoading = false,
}: HouseholdMemberFieldsProps) {
  const populated = Boolean(
    member.firstName?.trim() ||
      member.lastName?.trim() ||
      member.fileNumber?.trim() ||
      member.dateOfBirth ||
      [member.physicalAddress, member.mailingAddress].some(
        (address) =>
          address?.line1?.trim() ||
          address?.line2?.trim() ||
          address?.city?.trim() ||
          address?.postalCode?.trim(),
      ),
  );

  const { values } = useFormState<OtlFormSubmission>({
    subscription: { values: true },
  });
  return (
    <Box flex={1}>
      <Typography component="h3" variant="subtitle1">
        Family member
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextFormField
            name={`${name}.firstName`}
            label="First Name"
            required={populated}
            size="small"
            noIcon
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextFormField
            name={`${name}.lastName`}
            label="Last Name"
            required={populated}
            size="small"
            noIcon
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextFormField
            name={`${name}.fileNumber`}
            label="File # (optional)"
            size="small"
            noIcon
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DateFormField
            name={`${name}.dateOfBirth`}
            label="Date of birth (optional)"
            noIcon
            size="small"
          />
        </Grid>
      </Grid>
      <CheckboxFormField
        name={`${name}.sameAddressAsApplicant`}
        label="Same address as applicant"
      />
      <Typography variant="body2">
        This uses both the applicant’s physical and mailing addresses.
      </Typography>
      {member.sameAddressAsApplicant ? (
        <Box mt={1}>
          <Typography variant="body2">
            Physical address: {addressText(values.physicalAddress)}
          </Typography>
          <Typography variant="body2">
            Mailing address: {addressText(values.mailingAddress)}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2} mt={1}>
          {(["physicalAddress", "mailingAddress"] as const).map((kind) => (
            <Grid item xs={12} md={6} key={kind}>
              <OtlAddressFields
                name={`${name}.${kind}`}
                label={
                  kind === "physicalAddress"
                    ? "Physical Address"
                    : "Mailing Address"
                }
                required={populated}
                noIcon
                communities={communities}
                isCommunitiesLoading={isCommunitiesLoading}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

export interface HouseholdMemberListProps {
  communities?: ListOption[];
  isCommunitiesLoading?: boolean;
}
export type houseHoldMember = OtlHouseholdMember;
export function createHouseholdMember(): OtlHouseholdMember {
  return {
    memberId: crypto.randomUUID(),
    sameAddressAsApplicant: false,
    physicalAddress: { provinceOrTerritory: 12, country: "CA" },
    mailingAddress: { provinceOrTerritory: 12, country: "CA" },
  };
}
function addressText(address?: OtlAddress) {
  return (
    [
      address?.line1,
      address?.line2,
      address?.city === "Outside ISR" ? address.outsideIsrCity : address?.city,
      CANADIAN_PROVINCES.find(
        ({ id }) => String(id) === String(address?.provinceOrTerritory),
      )?.name ?? address?.provinceOrTerritory,
      address?.postalCode,
      address?.country,
    ]
      .filter(Boolean)
      .join(", ") || "Enter the applicant address above."
  );
}
export function HouseholdMemberList({
  communities,
  isCommunitiesLoading = false,
}: HouseholdMemberListProps) {
  return (
    <Box>
      <Typography color="text.secondary">Household Members</Typography>
      <FieldArray<OtlHouseholdMember>
        name="houseHoldMembers"
        render={({ fields }) => (
          <Stack spacing={2}>
            {fields.map((name, i) => {
              const member = fields.value?.[i] ?? ({} as OtlHouseholdMember);
              return (
                <Stack
                  direction="row"
                  spacing={1}
                  key={member.memberId ?? name}
                  mt={2}
                  p={2}
                  border="1px solid #ddd"
                  borderRadius={1}
                >
                  <HouseholdMemberFields
                    name={name}
                    member={member}
                    communities={communities}
                    isCommunitiesLoading={isCommunitiesLoading}
                  />
                  <Button
                    color="error"
                    size="small"
                    sx={{ minWidth: 0 }}
                    onClick={() => fields.remove(i)}
                  >
                    <DeleteIcon />
                  </Button>
                </Stack>
              );
            })}
            <Box>
              <Button
                onClick={() => fields.push(createHouseholdMember())}
                startIcon={<MuiAdd />}
                variant="outlined"
              >
                Add household member
              </Button>
            </Box>
          </Stack>
        )}
      />
    </Box>
  );
}
